import {
  IconBoxMultiple,
  IconBuildingCottage,
  IconFileTextSpark,
  IconScan,
  IconTools,
} from '@tabler/icons-react'
import { NavLink } from 'react-router-dom'
import { Box, Grid, Paper, rem, Stack, Title } from '@mantine/core'
import { PageContainer } from '@shared/components/app-shell/page-container/page-container'
import { PAGE_TITLES } from '@/constants/page-titles'
import { useContext } from 'react'
import { UserContext } from '@/providers/user-context'
import { UserRole } from '@/hooks/use-user-roles'
import { AlertLevel, IconAlert } from '@shared/components/icon-alert/icon-alert'

function RoleProtectedRoute({
  roles,
  allowedRoles,
  children,
}: {
  roles: UserRole[] | null
  allowedRoles: UserRole[]
  children: React.ReactNode
}) {
  // Allow the user to access the page if they have no roles yet. This allows for page reloads to work.
  if (!roles) return <>{children}</>

  // Check if the user has any of the allowed roles
  if (roles.some((role) => allowedRoles.includes(role))) return <>{children}</>

  // No allowed role: block access
  return <></>
}

export function HomePage() {
  const { roles } = useContext(UserContext)

  return (
    <PageContainer title={PAGE_TITLES.HOME}>
      <Stack>
        {/*
          Note: Home Page/Dashboard content to be finalised.
          The following should be componentised/styles moved to
          CSS module files etc if design direction is retained.
        */}
        {roles == null || roles.length > 0 ? (
          <>
            <Title order={2}>Quick links</Title>
            <Grid maw={rem(800)} aria-label='Quick link items'>
              <RoleProtectedRoute roles={roles} allowedRoles={['AllAccessUser']}>
                <Grid.Col span={{ base: 6, md: 4 }}>
                  <Paper
                    to='/stations'
                    component={NavLink}
                    shadow='sm'
                    p='md'
                    withBorder
                    bd='1px solid gray.2'
                  >
                    <Stack gap='xl'>
                      <Box c='blue.6'>
                        <IconBuildingCottage />
                      </Box>
                      <Title order={3} size='lg' c='black'>
                        Stations
                      </Title>
                    </Stack>
                  </Paper>
                </Grid.Col>
              </RoleProtectedRoute>
              <RoleProtectedRoute roles={roles} allowedRoles={['AllAccessUser']}>
                <Grid.Col span={{ base: 6, md: 4 }}>
                  <Paper
                    to='/offers'
                    component={NavLink}
                    shadow='sm'
                    p='md'
                    withBorder
                    bd='1px solid gray.2'
                  >
                    <Stack gap='xl'>
                      <Box c='blue.6'>
                        <IconFileTextSpark />
                      </Box>
                      <Title order={3} size='lg' c='black'>
                        Offers
                      </Title>
                    </Stack>
                  </Paper>
                </Grid.Col>
              </RoleProtectedRoute>
              <RoleProtectedRoute roles={roles} allowedRoles={['WoolStoreUser', 'AllAccessUser']}>
                <Grid.Col span={{ base: 6, md: 4 }}>
                  <Paper
                    to='/wool-store-tools'
                    component={NavLink}
                    shadow='sm'
                    p='md'
                    withBorder
                    bd='1px solid gray.2'
                  >
                    <Stack gap='xl'>
                      <Box c='blue.6'>
                        <IconTools />
                      </Box>
                      <Title order={3} size='lg' c='black'>
                        Wool Store Tools
                      </Title>
                    </Stack>
                  </Paper>
                </Grid.Col>
              </RoleProtectedRoute>
              <RoleProtectedRoute roles={roles} allowedRoles={['AllAccessUser']}>
                <Grid.Col span={{ base: 6, md: 4 }}>
                  <Paper
                    to='/typing/program'
                    component={NavLink}
                    shadow='sm'
                    p='md'
                    withBorder
                    bd='1px solid gray.2'
                  >
                    <Stack gap='xl'>
                      <Box c='blue.6'>
                        <IconScan />
                      </Box>
                      <Title order={3} size='lg' c='black'>
                        Typing
                      </Title>
                    </Stack>
                  </Paper>
                </Grid.Col>
              </RoleProtectedRoute>
              <RoleProtectedRoute roles={roles} allowedRoles={['AllAccessUser']}>
                <Grid.Col span={{ base: 6, md: 4 }}>
                  <Paper
                    to='/typing/typed-lots'
                    component={NavLink}
                    shadow='sm'
                    p='md'
                    withBorder
                    bd='1px solid gray.2'
                  >
                    <Stack gap='xl'>
                      <Box c='blue.6'>
                        <IconBoxMultiple />
                      </Box>
                      <Title order={3} size='lg' c='black'>
                        Typed Lots
                      </Title>
                    </Stack>
                  </Paper>
                </Grid.Col>
              </RoleProtectedRoute>
            </Grid>
          </>
        ) : (
          <IconAlert
            level={AlertLevel.ERROR}
            title='No Account Permissions'
            message='Please contact your system administrator to gain permissions to this application.'
          />
        )}
      </Stack>
    </PageContainer>
  )
}
